import { load } from 'scrivito'

import { FolderStackItem, DocumentsFilters } from '@/Components/Document/types'
import { Document } from '@/Data/Document/DocumentDataClass'
import { DocumentDocumentRelation } from '@/Data/DocumentDocumentRelation/DocumentDocumentRelationDataClass'
import { DataScopeOrder } from '@/Data/types'

interface useDocumentFolderNavigationProps {
  folderStack: FolderStackItem[]
  setFolderStack: React.Dispatch<React.SetStateAction<FolderStackItem[]>>
  filters: DocumentsFilters
  order: DataScopeOrder
  limit: number
  search: string
  setCount: React.Dispatch<React.SetStateAction<number>>
  setError: React.Dispatch<React.SetStateAction<unknown>>
  loadDocumentsFromRelation: (ids: string[]) => void
  loadDocumentsForEntity: () => void
}

/**
 * Hook that handles folder-based navigation logic for documents,
 * including opening a folder, going back/up in the stack, and resetting to root.
 *
 * @internal Do not use this hook directly. Use the DocumentContextProvider and
 * its exported context instead.
 *
 */
export const useDocumentFolderNavigation = ({
  folderStack,
  setFolderStack,
  filters,
  order,
  limit,
  search,
  setCount,
  setError,
  loadDocumentsFromRelation,
  loadDocumentsForEntity,
}: useDocumentFolderNavigationProps) => {
  const openFolder = (folderId: string) => {
    load(() => {
      try {
        const scope = DocumentDocumentRelation.all()
        const transform = {
          filters: {
            father: folderId,
            document_type: filters.type.value,
            document_search: search,
          },
          order: [order],
          limit,
        }
        const transformed = scope.transform(transform)
        const ids = transformed.take().map((e) => e.get('child') as string)
        const folder = Document.get(folderId)
        const foldername = (folder?.get('displayTitle') as string) || 'Folder'

        setFolderStack((stack) => {
          const alreadyTop = stack[stack.length - 1]?.id === folderId
          return alreadyTop
            ? stack
            : [...stack, { id: folderId, name: foldername }]
        })
        loadDocumentsFromRelation(ids)
        setCount(transformed.count() || 0)
      } catch (e) {
        setError(e)
      }
    })
  }

  const goToRoot = () => {
    setFolderStack([])
    loadDocumentsForEntity()
  }

  const goBackFolder = () => {
    if (folderStack.length > 1) {
      const newStack = [...folderStack]
      newStack.pop()
      const previous = newStack[newStack.length - 1]
      setFolderStack(newStack)

      if (previous) {
        openFolder(previous.id)
      } else {
        goToRoot()
      }
    }
  }

  const goToFolder = (folderId: string) => {
    const index = folderStack.findIndex((f) => f.id === folderId)
    if (index !== -1) {
      const newStack = folderStack.slice(0, index + 1)
      setFolderStack(newStack)
      openFolder(folderId)
    }
  }

  return { openFolder, goToRoot, goBackFolder, goToFolder }
}
